import { useCallback, useState } from "react";
import {
  readStoredDisplayCurrency,
  useExchangeRateOptional,
} from "../contexts/exchangeRate";

const EMPTY_PREVIEW = {
  open: false,
  title: "",
  url: null,
  blob: null,
  fileName: "",
  loading: false,
  error: "",
};

async function resolvePdfCurrencyOptions(fx) {
  const displayCurrency = fx?.displayCurrency ?? readStoredDisplayCurrency();
  let exchangeRate = fx?.rate ?? null;

  if (displayCurrency === "TRY" && !exchangeRate && fx?.fetchRate) {
    exchangeRate = await fx.fetchRate();
  }

  if (displayCurrency === "TRY" && !exchangeRate) {
    throw new Error("Kur yüklenemedi — TL fiyatlı PDF oluşturulamıyor.");
  }

  return { displayCurrency, exchangeRate };
}

/** Shared PDF preview state — generate receives currency options from header toggle. */
export function usePdfPreview() {
  const fx = useExchangeRateOptional();
  const [pdfPreview, setPdfPreview] = useState(EMPTY_PREVIEW);

  const closePdfPreview = useCallback(() => {
    setPdfPreview((prev) => {
      if (prev.url) URL.revokeObjectURL(prev.url);
      return { ...EMPTY_PREVIEW };
    });
  }, []);

  const openPdfPreview = useCallback(
    async ({ title = "PDF Önizleme", generate }) => {
      setPdfPreview((prev) => {
        if (prev.url) URL.revokeObjectURL(prev.url);
        return {
          ...EMPTY_PREVIEW,
          open: true,
          title,
          loading: true,
        };
      });

      try {
        const currencyOptions = await resolvePdfCurrencyOptions(fx);
        const result = await generate(currencyOptions);
        if (!result?.blob) {
          throw new Error("PDF oluşturulamadı");
        }
        const url = URL.createObjectURL(result.blob);
        setPdfPreview((prev) => ({
          ...prev,
          url,
          blob: result.blob,
          fileName: result.fileName || "document.pdf",
          loading: false,
        }));
      } catch (err) {
        setPdfPreview((prev) => ({
          ...prev,
          loading: false,
          error: err?.message || "PDF oluşturulamadı",
        }));
      }
    },
    [fx],
  );

  const downloadPdfFromPreview = useCallback(() => {
    const { blob, fileName, url } = pdfPreview;
    if (!blob && !url) return;
    const link = document.createElement("a");
    link.href = url || URL.createObjectURL(blob);
    link.download = fileName || "document.pdf";
    document.body.appendChild(link);
    link.click();
    link.remove();
    if (!url && blob) URL.revokeObjectURL(link.href);
  }, [pdfPreview]);

  return {
    pdfPreview,
    closePdfPreview,
    openPdfPreview,
    downloadPdfFromPreview,
  };
}
